/**
 * Utility condivisa per il sort dichiarativo nelle tabelle Diakonix.
 *
 * Estratta in v2.12.6 dal pattern introdotto da Gabri in v2.12.5
 * (`fornitori-ordini-list` + `storico-parziali`), che era duplicato
 * in-line in ogni componente. Centralizzandolo qui evitiamo drift
 * delle euristiche tra tabelle (date detection, numeric coercion,
 * case-insensitive string compare).
 *
 * Pattern di utilizzo nel componente:
 *
 *   import { SortDirection, sortItems } from 'src/app/utils/table-sort';
 *
 *   public sortColumn: string | null = null;
 *   public sortDirection: SortDirection = 'asc';
 *
 *   private applySort(items: MyType[]): MyType[] {
 *     if (!this.sortColumn) return items;
 *     return sortItems(items, this.sortColumn, this.sortDirection);
 *   }
 *
 *   sortBy(column: string): void {
 *     if (this.sortColumn === column) {
 *       this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
 *     } else {
 *       this.sortColumn = column;
 *       this.sortDirection = 'asc';
 *     }
 *     this.applyView(); // o re-filter/re-render del template
 *   }
 *
 * Nel template HTML:
 *
 *   <th class="col-X" (click)="sortBy('field')">
 *     Label
 *     @if (sortColumn === 'field') {
 *       <span>{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
 *     }
 *   </th>
 *
 * SCSS shared: in v2.12.5 sono state aggiunte ~10 LOC a 14 file scss
 * delle pagine con tabelle (cursor:pointer su thead + hover state).
 * Vedi `fornitori-ordini-list.component.scss` come reference.
 */

export type SortDirection = 'asc' | 'desc';

/**
 * Ordina una copia di `items` per la colonna indicata.
 *
 * Detection automatica del tipo (precedenza decrescente):
 * 1. **Date**: `Date instance` OR col name contiene `data`/`date`
 *    (euristica legacy di Gabri) OR valore parsabile come ISO date.
 * 2. **Number**: entrambi i valori parsabili via `Number()` e non NaN.
 * 3. **String**: fallback con case-insensitive compare.
 *
 * Null/undefined sono trattati come stringa vuota (vanno in cima per
 * `asc`, in fondo per `desc`).
 */
export function sortItems<T>(
  items: T[],
  column: keyof T | string,
  direction: SortDirection,
): T[] {
  if (!items?.length || !column) return items ?? [];

  const colKey = String(column);
  const isDateColumnHint =
    colKey.toLowerCase().includes('data') ||
    colKey.toLowerCase().includes('date');

  return [...items].sort((a: any, b: any) => {
    let valueA = a?.[colKey];
    let valueB = b?.[colKey];

    valueA ??= '';
    valueB ??= '';

    // ── Date detection ─────────────────────────────────────────────
    const isDateInstance =
      valueA instanceof Date || valueB instanceof Date;
    const isDateString =
      (typeof valueA === 'string' && /^\d{4}-\d{2}-\d{2}/.test(valueA)) ||
      (typeof valueB === 'string' && /^\d{4}-\d{2}-\d{2}/.test(valueB));

    if (isDateInstance || isDateColumnHint || isDateString) {
      const tA = valueA ? new Date(valueA).getTime() : 0;
      const tB = valueB ? new Date(valueB).getTime() : 0;
      const result = tA > tB ? 1 : tA < tB ? -1 : 0;
      return direction === 'asc' ? result : -result;
    }

    // ── Number detection ───────────────────────────────────────────
    const nA = Number(valueA);
    const nB = Number(valueB);
    const isNumeric =
      !Number.isNaN(nA) && valueA !== '' &&
      !Number.isNaN(nB) && valueB !== '';

    if (isNumeric) {
      const result = nA > nB ? 1 : nA < nB ? -1 : 0;
      return direction === 'asc' ? result : -result;
    }

    // ── String fallback (case insensitive) ─────────────────────────
    const sA = String(valueA).toLowerCase();
    const sB = String(valueB).toLowerCase();
    const result = sA > sB ? 1 : sA < sB ? -1 : 0;
    return direction === 'asc' ? result : -result;
  });
}

/**
 * Helper per il toggle classico onClick di un header colonna.
 * Restituisce il nuovo stato `{ column, direction }`.
 *
 * Use:
 *   const next = nextSortState(this.sortColumn, this.sortDirection, 'or_data');
 *   this.sortColumn = next.column;
 *   this.sortDirection = next.direction;
 */
export function nextSortState(
  currentColumn: string | null,
  currentDirection: SortDirection,
  clickedColumn: string,
): { column: string; direction: SortDirection } {
  if (currentColumn === clickedColumn) {
    return {
      column: clickedColumn,
      direction: currentDirection === 'asc' ? 'desc' : 'asc',
    };
  }
  return { column: clickedColumn, direction: 'asc' };
}
